import * as React from 'react';
import * as moment from 'moment';
import i18n from 'src/translation/i18n';

import Select from 'src/components/common/Select2';
import SlideMenu from 'src/components/common/SlideMenu';
import DateRange from 'src/components/common/DateRange';
import DateRangeSeparated from 'src/components/common/DateRangeSeparated';
import SortCard from 'src/components/common/SortCard';

import { isMobile } from 'src/utils/responsiveHelpers';
import { ISelectOption } from 'src/components/common/Select2/components/SelectOption/SelectOption';
import './FiltersBar.scss';

interface IFiltersBarProps {
  showSlideMenu: boolean;
  showClearFilter: boolean;
  status: ISelectOption;
  searchOption: ISelectOption;
  searchText: string;
  dates: { start: any; end: any };
  handleFilterByStatus: (status: ISelectOption) => void;
  handleSearchSelect: (searchOption: ISelectOption) => void;
  handleSearchText: (e: any) => void;
  handleOnClickSearch: () => void;
  handleClearFilters: () => void;
  handleSearchByDates: (dates: { start: any; end: any }) => void;
  handleClearDates: () => void;
  onCloseSlideMenu: () => void;
  onOpenSlideMenu: () => void;
  handleSearch: (
    startDate: moment.Moment,
    endDate: moment.Moment,
    sortByOption: string,
    sortByOrder: string,
    status: ISelectOption
  ) => void;
  setDates: (dates: { start: any; end: any }) => void;
  t?: any;
}

interface IFiltersBarState {
  isMobile: boolean;
  sortByOption: 'orderDate' | 'status';
  sortByOrder: 'ASC' | 'DESC';
  status: ISelectOption;
}

class FiltersBar extends React.Component<IFiltersBarProps, IFiltersBarState> {
  constructor(props: any) {
    super(props);

    this.state = {
      isMobile: isMobile(),
      sortByOption: 'orderDate',
      sortByOrder: 'ASC',
      status: this.props.status
    };
  }

  public componentWillMount() {
    window.addEventListener('resize', this.handleWindowSizeChange);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowSizeChange);
  }

  public handleWindowSizeChange = () => {
    const isMob = isMobile();
    if (isMob !== this.state.isMobile) {
      this.setState({ isMobile: isMob });
    }
  };

  private onSortByChange = (sort: {
    sortBy: 'orderDate' | 'status';
    sortIn: 'asc' | 'desc';
    label: string;
  }): void => {
    this.setState({
      sortByOption: sort.sortBy,
      sortByOrder: sort.sortIn === 'asc' ? 'ASC' : 'DESC'
    });
  };

  private onStatusChange = (status: ISelectOption) => {
    this.setState({ status });
  };

  private handleSaveFilters = () => {
    this.props.handleSearch(
      this.props.dates ? this.props.dates.start : null,
      this.props.dates ? this.props.dates.end : null,
      this.state.sortByOption,
      this.state.sortByOrder,
      this.state.status
    );
    this.props.onCloseSlideMenu();
  };

  private renderFilters = () => {
    const { t } = this.props;
    return (
      <div className="rp-filters-bar--filter">
        {this.state.isMobile ? (
          <React.Fragment>
            <div
              className="rp-filters-bar--filter-button"
              onClick={this.props.onOpenSlideMenu}
            >
              <span className="rpds-sort" /> Filters and sorting
            </div>
            <SlideMenu
              mounted={this.props.showSlideMenu}
              goBackTitle={t('common.backToResults')}
              level={2 + 30}
              handleCloseMenu={this.props.onCloseSlideMenu}
            >
              <div className="rp-filters-bar--filter--content">
                <div>
                  <Select
                    options={[
                      { name: `${t('myOrders.filterKeys.all')}`, value: null },
                      {
                        name: `${t('myOrders.filterKeys.ordersPending')}`,
                        value: 'OP'
                      },
                      {
                        name: `${t('myOrders.filterKeys.allBackOrders')}`,
                        value: 'BO'
                      },
                      {
                        name: `${t('myOrders.filterKeys.allCredits')}`,
                        value: 'CR'
                      },
                      {
                        name: `${t('myOrders.filterKeys.invoiced')}`,
                        value: 'IN'
                      },
                      {
                        name: `${t('myOrders.filterKeys.creditsReturn')}`,
                        value: 'RS'
                      },
                      {
                        name: `${t('myOrders.filterKeys.creditsWarranty')}`,
                        value: 'RW'
                      }
                    ]}
                    selected={this.state.status}
                    label={'Filter by:'}
                    onSelect={this.onStatusChange}
                  />
                  <div className="date-separated">
                    <DateRangeSeparated
                      onDatesSelected={this.props.setDates}
                      dates={this.props.dates}
                    />
                  </div>
                  <div className="rp-filters-bar--filter-sort-by">
                    <label className="rp-filters-bar--filter-sort-by--title">
                      Sort by:
                    </label>
                    <div className="rp-filters-bar--filter-sort-by--list">
                      <SortCard
                        setSort={this.onSortByChange}
                        sort={{
                          sortBy: 'orderDate',
                          sortIn: 'desc',
                          label: `${t('myOrders.orderDate')} - (${t(
                            'common.newestToOldest'
                          )})`
                        }}
                        active={
                          this.state.sortByOption === 'orderDate' &&
                          this.state.sortByOrder === 'DESC'
                        }
                      />
                      <SortCard
                        setSort={this.onSortByChange}
                        sort={{
                          sortBy: 'orderDate',
                          sortIn: 'asc',
                          label: `${t('myOrders.orderDate')} - (${t(
                            'common.oldestToNewest'
                          )})`
                        }}
                        active={
                          this.state.sortByOption === 'orderDate' &&
                          this.state.sortByOrder === 'ASC'
                        }
                      />
                      <SortCard
                        setSort={this.onSortByChange}
                        sort={{
                          sortBy: 'status',
                          sortIn: 'asc',
                          label: `${t('myOrders.status')} - (${t(
                            'common.pendingAllCredits'
                          )})`
                        }}
                        active={
                          this.state.sortByOption === 'status' &&
                          this.state.sortByOrder === 'ASC'
                        }
                      />
                      <SortCard
                        setSort={this.onSortByChange}
                        sort={{
                          sortBy: 'status',
                          sortIn: 'desc',
                          label: `${t('myOrders.status')} - (${t(
                            'common.allCreditsPending'
                          )})`
                        }}
                        active={
                          this.state.sortByOption === 'status' &&
                          this.state.sortByOrder === 'DESC'
                        }
                      />
                    </div>
                  </div>
                </div>
                <button
                  className="rp-filters-bar--filter--save primary-button"
                  onClick={this.handleSaveFilters}
                >
                  {t('common.saveFilters')}
                </button>
              </div>
            </SlideMenu>
          </React.Fragment>
        ) : (
          <div className="rp-filters-bar--filter--desktop">
            <Select
              options={[
                { name: `${t('myOrders.filterKeys.all')}`, value: null },
                {
                  name: `${t('myOrders.filterKeys.ordersPending')}`,
                  value: 'OP'
                },
                {
                  name: `${t('myOrders.filterKeys.allBackOrders')}`,
                  value: 'BO'
                },
                { name: `${t('myOrders.filterKeys.allCredits')}`, value: 'CR' },
                { name: `${t('myOrders.filterKeys.invoiced')}`, value: 'IN' },
                { name: `${t('myOrders.filterKeys.purchases')}`, value: 'NC' },
                {
                  name: `${t('myOrders.filterKeys.creditsReturn')}`,
                  value: 'RS'
                },
                {
                  name: `${t('myOrders.filterKeys.creditsWarranty')}`,
                  value: 'RW'
                }
              ]}
              selected={this.props.status}
              label={t('myOrders.filterTitle')}
              onSelect={this.props.handleFilterByStatus}
            />
            <div className="date">
              <DateRange
                onDatesSelected={this.props.handleSearchByDates}
                dates={this.props.dates}
                onClear={this.props.handleClearDates}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  private renderClearFilters = () => {
    const { t } = this.props;
    return (
      <div className="rp-results-counter">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 d-flex flex-column flex-md-row justify-content-between">
              <p>
                {/* <span className="rp-results-counter-values">
                {resultsQuantity}
              </span>{' '}
              {i18n.t('common.search')} {i18n.t('common.results')}{' '}
              {i18n.t('common.for')}{' '}
              <span className="rp-results-counter-values">{item}</span> */}
              </p>
              <button
                onClick={this.props.handleClearFilters}
                className="rp-results-counter-clear"
              >
                <span className="" />
                <label>{t('common.clearSearch')}</label>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  public render() {
    const { t } = this.props;
    return (
      <div className="rp-filters-bar">
        {this.renderFilters()}
        <div className="rp-filters-bar--search">
          <Select
            options={[
              {
                name: `${t('myOrders.searchByKeys.orderNumber')}`,
                value: 'orderNumber'
              },
              {
                name: `${t('myOrders.searchByKeys.partNumber')}`,
                value: 'partNumber'
              },
              {
                name: `${t('myOrders.searchByKeys.poNumber')}`,
                value: 'poNumber'
              },
              {
                name: `${t('myOrders.searchByKeys.invoiceNumber')}`,
                value: 'invoiceNumber'
              },
              {
                name: `${t('myOrders.searchByKeys.shipmentMarking')}`,
                value: 'shipmentMarking'
              },
              {
                name: `${t('myOrders.searchByKeys.confirmationNumber')}`,
                value: 'confirmationNumber'
              }
            ]}
            selected={this.props.searchOption}
            label={`${t('common.searchBy')}:`}
            onSelect={this.props.handleSearchSelect}
          />
          <div className="rp-filters-bar--search-text">
            <input aria-label="Search your orders"
              className="rp-filters-bar--search-text--input"
              onChange={this.props.handleSearchText}
              value={this.props.searchText}
              onKeyPress={event => {
                if (event.key === 'Enter') {
                  this.props.handleOnClickSearch();
                }
              }}
            />
            <button
              onClick={this.props.handleOnClickSearch}
              className="primary-button rp-filters-bar--search-text--button"
            >
              <span className="rpds-search" />
              <label>{t('common.search')}</label>
            </button>
          </div>
        </div>
        {this.props.showClearFilter === true ? (
          <React.Fragment>{this.renderClearFilters()}</React.Fragment>
        ) : null}
      </div>
    );
  }
}

export default FiltersBar;
